import { useState, useEffect } from 'react';
import api from '../api/axios';

export default function usePushNotifications() {
    const [permission, setPermission] = useState(Notification?.permission || 'default');
    const [subscribed, setSubscribed] = useState(false);
    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState('');

    // Check existing subscription status on mount
    useEffect(() => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        api.get('/push/status')
            .then(r => setSubscribed(r.data.active))
            .catch(() => {});
    }, []);

    const subscribe = async () => {
        setError('');
        setLoading(true);
        try {
            // 1. Request permission
            const result = await Notification.requestPermission();
            setPermission(result);
            if (result !== 'granted') {
                setError('Notification permission denied. Please enable in browser settings.');
                return;
            }

            // 2. Get VAPID public key from backend
            const { data: keyData } = await api.get('/push/vapid-public-key');
            if (!keyData.publicKey) throw new Error('Push notifications not configured on server');

            // 3. Get service worker registration
            const registration = await navigator.serviceWorker.ready;

            // 4. Subscribe to push
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly:      true,
                applicationServerKey: urlBase64ToUint8Array(keyData.publicKey),
            });

            // 5. Save to backend
            const sub = subscription.toJSON();
            await api.post('/push/subscribe', {
                endpoint: sub.endpoint,
                keys:     sub.keys,
            });

            setSubscribed(true);
        } catch (err) {
            setError(err.message || 'Failed to enable notifications');
        } finally {
            setLoading(false);
        }
    };

    const unsubscribe = async () => {
        setLoading(true);
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await api.delete('/push/unsubscribe', { data: { endpoint: subscription.endpoint } });
                await subscription.unsubscribe();
            }
            setSubscribed(false);
        } catch (err) {
            setError(err.message || 'Failed to disable notifications');
        } finally {
            setLoading(false);
        }
    };

    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

    return { subscribe, unsubscribe, subscribed, permission, loading, error, isSupported };
}

// Utility: convert base64 VAPID key to Uint8Array for browser API
function urlBase64ToUint8Array(base64String) {
    const padding  = '='.repeat((4 - base64String.length % 4) % 4);
    const base64   = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData  = window.atob(base64);
    const arr      = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) arr[i] = rawData.charCodeAt(i);
    return arr;
}